import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import MetricCard from "../../performance/MetricCard";
import PieChart from "../../performance/PieChart";
import {
  PerformanceContainer,
  MetricsGrid,
  ChartsGrid,
} from "../../performance/PerformanceStyles";
import {
  fetchTrafficMetrics,
  fetchPieChartData,
  fetchDateYearMetrics,
} from "store/performance-report/PerformanceReportActions";
import { MetricCategory } from "store/performance-report/PerformanceReportTypes";
import {
  useSetGoalHandler,
  useDeleteGoalHandler,
} from "hooks/useSetGoalHandler";
import {
  selectTrafficMetrics,
  selectPerformanceReportStoreId,
  selectPerformanceReportDateRange,
  selectIsReportAPIAvailable,
  selectDateYearMetrics,
  selectPieChartDataByMetricId,
  selectIsTrafficMetricsAlreadyFetched,
} from "store/performance-report/PerformanceReportSelectors";
import {
  selectKpiCategoryById,
  selectKpiConfigIsLoaded,
} from "store/kpi-config/KpiConfigSelectors";
import {
  getBasicMetricsConfig,
  getPieChartMetricsConfig,
  convertBasicMetricsToPayload,
  getAllMetricIds,
  getMetricsByDisplayType,
  getAllPieChartApiConfigs,
  getMetricGoal,
  isMetricGoalEnabled,
} from "utils/KpiConfigUtils";
import {
  calculateDateRanges,
  transformMetricsData,
  transformPieChartsData,
  getDaysFromPeriodType,
  calculateDimensionId,
  calculateGranularity,
  createMetricLineCharts,
} from "utils/PerformanceReportUtils";
import AppState from "store/AppState";

interface TrafficComponentProps {}

const TrafficComponent: React.FC<TrafficComponentProps> = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const theme = useTheme();
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const performanceDateRange = useSelector(selectPerformanceReportDateRange);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const trafficMetricsData = useSelector(selectTrafficMetrics);
  const dateYearMetricsData = useSelector(
    selectDateYearMetrics(MetricCategory.TRAFFIC),
  );
  const isKpiConfigLoaded = useSelector(selectKpiConfigIsLoaded);
  const trafficCategory = useSelector((state: any) =>
    selectKpiCategoryById("traffic")(state),
  );
  const isAlreadyFetched = useSelector(selectIsTrafficMetricsAlreadyFetched);

  const trafficMetricsConfig = useMemo(() => {
    return isKpiConfigLoaded && trafficCategory
      ? getBasicMetricsConfig(trafficCategory)
      : [];
  }, [isKpiConfigLoaded, trafficCategory]);

  const trafficPieChartsConfig = useMemo(() => {
    return isKpiConfigLoaded && trafficCategory
      ? getPieChartMetricsConfig(trafficCategory)
      : [];
  }, [isKpiConfigLoaded, trafficCategory]);

  const pieChartMetricIds = useMemo(() => {
    return trafficPieChartsConfig.map((config: any) => config.id);
  }, [trafficPieChartsConfig]);

  const pieChartsData = useSelector((state: AppState) => {
    const dataMap: Record<string, { data: any; isLoading: boolean }> = {};
    pieChartMetricIds.forEach((metricId: string) => {
      dataMap[metricId] = selectPieChartDataByMetricId(metricId)(state);
    });
    return dataMap;
  });

  const trafficMetrics = transformMetricsData(
    trafficMetricsConfig,
    trafficMetricsData,
    intl,
  );

  const trafficPieCharts = transformPieChartsData(
    trafficPieChartsConfig,
    pieChartsData,
    intl,
    theme,
  );

  const dateRangeDays = getDaysFromPeriodType(performanceDateRange);
  const dimensionId = calculateDimensionId(dateRangeDays);
  const granularity = calculateGranularity(dateRangeDays);
  const metricLineCharts = createMetricLineCharts(
    dateYearMetricsData,
    trafficMetricsConfig,
    granularity,
    intl,
    "traffic",
  );

  const handleSetGoal = useSetGoalHandler("traffic");
  const handleDeleteGoal = useDeleteGoalHandler("traffic");

  useEffect(() => {
    if (isAlreadyFetched) {
      return;
    }

    if (
      performanceStoreId &&
      performanceDateRange &&
      isReportAPIAvailable &&
      isKpiConfigLoaded &&
      trafficCategory
    ) {
      const { currentPeriod, comparisonPeriod } =
        calculateDateRanges(performanceDateRange);
      const basicMetrics = getMetricsByDisplayType(trafficCategory, "basic");
      const metricsPayload = convertBasicMetricsToPayload(basicMetrics);

      dispatch(
        fetchTrafficMetrics(
          performanceStoreId,
          currentPeriod,
          comparisonPeriod,
          metricsPayload,
        ),
      );

      const pieChartConfigs = getAllPieChartApiConfigs(trafficCategory);
      pieChartConfigs.forEach((config) => {
        dispatch(
          fetchPieChartData({
            storeId: performanceStoreId,
            metricId: config.metricId,
            dateRange: currentPeriod,
            dimension: config.dimension,
            metrics: config.metrics,
            sortBy: config.sortBy,
            limit: config.limit,
          }),
        );
      });

      const allMetricIds = getAllMetricIds(basicMetrics);
      dispatch(
        fetchDateYearMetrics({
          storeId: performanceStoreId,
          dateRange: currentPeriod,
          dimension: { id: dimensionId },
          metrics: allMetricIds,
          category: MetricCategory.TRAFFIC,
        }),
      );
    }
  }, [
    dispatch,
    performanceStoreId,
    performanceDateRange,
    isReportAPIAvailable,
    isKpiConfigLoaded,
    trafficCategory,
    dateRangeDays,
    dimensionId,
    isAlreadyFetched,
  ]);

  return (
    <>
      {performanceStoreId && performanceDateRange && (
        <PerformanceContainer>
          <MetricsGrid>
            {trafficMetrics.map((metric) => {
              const goalEnabled = isMetricGoalEnabled(
                trafficCategory,
                metric.id,
              );
              return (
                <MetricCard
                  key={metric.id}
                  data={metric}
                  lineChartData={metricLineCharts[metric.id]}
                  onSetGoal={goalEnabled ? handleSetGoal : undefined}
                  onDeleteGoal={goalEnabled ? handleDeleteGoal : undefined}
                  goal={
                    goalEnabled
                      ? getMetricGoal(trafficCategory, metric.id)
                      : undefined
                  }
                />
              );
            })}
          </MetricsGrid>
          <ChartsGrid>
            {trafficPieCharts.map((chartData, index) => (
              <PieChart key={index} data={chartData} />
            ))}
          </ChartsGrid>
        </PerformanceContainer>
      )}
    </>
  );
};

export default TrafficComponent;
